import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const PublishScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formId = searchParams?.get('id') || searchParams?.get('formId');
  const APP_BASE_URL = window.location?.origin;
  const LIVE_URL = formId ? `${APP_BASE_URL}/${formId}` : `${APP_BASE_URL}/wf_your-form-id`;
  const PREVIEW_URL = formId ? `${APP_BASE_URL}/${formId}?preview=true` : null;

  const embedSnippets = {
    javascript: `<script src="${APP_BASE_URL}/embed.js"></script>
<script>
  WebForms.embed({
    formId: '${formId || 'your-form-id'}',
    container: '#form-container',
    autoResize: true,
  });
</script>
<div id="form-container"></div>`,
    iframe: `<iframe
  src="${LIVE_URL}"
  width="100%"
  height="600"
  frameborder="0"
  style="border:none;border-radius:8px;"
  title="Web Form">
</iframe>`,
    react: `import { WebFormsEmbed } from '@webforms/react';

export default function App() {
  return (
    <WebFormsEmbed
      formId="${formId || 'your-form-id'}"
      autoResize
      onSubmit={(data) => console.log(data)}
    />
  );
}`,
    html: `<div data-webforms-embed="${formId || 'your-form-id'}"></div>
<script async src="${APP_BASE_URL}/embed.js"></script>`,
  };

  const readinessChecks = [
    { id: 1, label: 'Form fields configured', status: 'pass', detail: '6 fields added' },
    { id: 2, label: 'Submission review ready', status: 'pass', detail: 'Responses will appear in your dashboard' },
    { id: 3, label: 'Form validated (no errors)', status: 'pass', detail: 'All required fields present' },
    { id: 4, label: 'HTTPS / secure delivery', status: 'pass', detail: 'SSL certificate active' },
    { id: 5, label: 'Spam protection enabled', status: 'warn', detail: 'Consider adding reCAPTCHA' },
    { id: 6, label: 'Mobile responsive', status: 'pass', detail: 'Tested on all breakpoints' },
  ];

  const [isPublished, setIsPublished] = useState(false);
  const [activeEmbed, setActiveEmbed] = useState('javascript');
  const [urlCopied, setUrlCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Load actual published state from DB on mount
  useEffect(() => {
    if (!formId) {
      setLoadingStatus(false);
      return;
    }
    const fetchPublishedState = async () => {
      try {
        const { data } = await supabase
          ?.from('forms')
          ?.select('published')
          ?.eq('id', formId)
          ?.maybeSingle();
        if (data) {
          setIsPublished(data?.published === true);
        }
      } catch (err) {
        console.error('Error fetching publish state:', err);
      } finally {
        setLoadingStatus(false);
      }
    };
    fetchPublishedState();
  }, [formId]);

  const passCount = readinessChecks?.filter(c => c?.status === 'pass')?.length;
  const warnCount = readinessChecks?.filter(c => c?.status === 'warn')?.length;
  const readinessScore = Math.round((passCount / readinessChecks?.length) * 100);

  const handleTogglePublish = async () => {
    if (!formId) return;
    setPublishing(true);
    const newState = !isPublished;
    try {
      const { error } = await supabase
        ?.from('forms')
        ?.update({ published: newState, updated_at: new Date()?.toISOString() })
        ?.eq('id', formId);
      if (!error) {
        setIsPublished(newState);
      } else {
        console.error('Publish toggle error:', error?.message);
      }
    } catch (err) {
      console.error('Publish toggle error:', err);
    } finally {
      setPublishing(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard?.writeText(LIVE_URL);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const shareLinks = [
    { label: 'Email', icon: 'Mail', href: `mailto:?subject=Check out this form&body=${LIVE_URL}` },
    { label: 'Twitter/X', icon: 'Twitter', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(LIVE_URL)}&text=Fill out my form!` },
    { label: 'LinkedIn', icon: 'Linkedin', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(LIVE_URL)}` },
    { label: 'WhatsApp', icon: 'MessageCircle', href: `https://wa.me/?text=${encodeURIComponent('Fill out my form: ' + LIVE_URL)}` },
  ];

  const embedTabs = [
    { id: 'javascript', label: 'JS Snippet', icon: 'Code2' },
    { id: 'iframe', label: 'iFrame', icon: 'Frame' },
    { id: 'react', label: 'React', icon: 'Component' },
    { id: 'html', label: 'HTML', icon: 'FileCode' },
  ];

  const CodeBlock = ({ code, language }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard?.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div className="relative rounded-lg overflow-hidden border border-border bg-[#0f1117]">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
          <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">{language}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={copied ? 'Check' : 'Copy'} size={13} color={copied ? '#0e7572' : undefined} />
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
        <pre className="p-4 text-xs font-mono text-green-300 overflow-x-auto leading-relaxed whitespace-pre">
          {code}
        </pre>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar stats={{}}>
        {/* Page header */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-8">

          {/* Top bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="ArrowLeft" size={18} />
                </button>
                <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                  Publish
                </h1>
              </div>
              <p className="text-sm text-muted-foreground ml-7">
                Deploy your form and share it with the world
              </p>
            </div>

            {/* Publish toggle */}
            <div className="flex items-center gap-3">
              {loadingStatus ? (
                <div className="w-5 h-5 border-2 border-[#0e7572] border-t-transparent rounded-full animate-spin" />
              ) : (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${
                  isPublished
                    ? 'bg-[#0e7572]/10 text-[#0e7572] border-[#0e7572]/30'
                    : 'bg-muted text-muted-foreground border-border'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${isPublished ? 'bg-[#0e7572] animate-pulse' : 'bg-muted-foreground'}`} />
                  {isPublished ? 'Live' : 'Draft'}
                </div>
              )}
              <button
                onClick={handleTogglePublish}
                disabled={publishing || loadingStatus || !formId}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  isPublished
                    ? 'bg-muted text-foreground hover:bg-muted/80 border border-border'
                    : 'bg-[#0e7572] text-white hover:bg-[#0b5f5c] shadow-md'
                } disabled:opacity-60`}
              >
                {publishing ? (
                  <Icon name="Loader2" size={16} className="animate-spin" />
                ) : (
                  <Icon name={isPublished ? 'EyeOff' : 'Globe'} size={16} />
                )}
                {publishing ? 'Updating…' : isPublished ? 'Unpublish' : 'Publish Now'}
              </button>
            </div>
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* LEFT COLUMN */}
            <div className="lg:col-span-2 space-y-6">

              {/* Live URL card */}
              <div className="bg-card border border-border rounded-xl p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0e7572]/10 flex items-center justify-center">
                    <Icon name="Link" size={16} color="#0e7572" />
                  </div>
                  <h2 className="text-base font-heading font-semibold text-foreground">Live Form URL</h2>
                </div>

                <div className="flex items-center gap-2 p-3 bg-muted/50 border border-border rounded-lg">
                  <Icon name="Globe" size={15} color="#0e7572" />
                  <span className="flex-1 text-sm font-mono text-foreground truncate">{LIVE_URL}</span>
                  <button
                    onClick={handleCopyUrl}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0e7572] text-white text-xs font-medium rounded-md hover:bg-[#0b5f5c] transition-colors shrink-0"
                  >
                    <Icon name={urlCopied ? 'Check' : 'Copy'} size={13} />
                    {urlCopied ? 'Copied!' : 'Copy'}
                  </button>
                  {/* Preview button — bypasses published gate so you can always preview */}
                  {PREVIEW_URL && (
                    <a
                      href={PREVIEW_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0e7572]/10 border border-[#0e7572]/30 text-[#0e7572] text-xs font-medium rounded-md hover:bg-[#0e7572]/20 transition-colors shrink-0"
                    >
                      <Icon name="Eye" size={13} />
                      Preview
                    </a>
                  )}
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-muted border border-border text-foreground text-xs font-medium rounded-md hover:bg-muted/80 transition-colors shrink-0"
                  >
                    <Icon name="ExternalLink" size={13} />
                    Open
                  </a>
                </div>

                {/* One-click share */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">Share instantly</p>
                  <div className="flex flex-wrap gap-2">
                    {shareLinks?.map(link => (
                      <a
                        key={link?.label}
                        href={link?.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground hover:bg-muted/80 hover:border-[#0e7572]/40 transition-all"
                      >
                        <Icon name={link?.icon} size={15} color="#0e7572" />
                        <span>{link?.label}</span>
                      </a>
                    ))}
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({ title: 'Web Form', url: LIVE_URL });
                        } else {
                          handleCopyUrl();
                        }
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-[#0e7572]/10 border border-[#0e7572]/30 rounded-lg text-sm text-[#0e7572] hover:bg-[#0e7572]/20 transition-all"
                    >
                      <Icon name="Share2" size={15} color="#0e7572" />
                      <span>More</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Embed code card */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 p-5 border-b border-border">
                  <div className="w-8 h-8 rounded-lg bg-[#0e7572]/10 flex items-center justify-center">
                    <Icon name="Code2" size={16} color="#0e7572" />
                  </div>
                  <h2 className="text-base font-heading font-semibold text-foreground">Embed Code</h2>
                  <span className="ml-auto text-xs text-muted-foreground">Add to any website</span>
                </div>

                {/* Embed method tabs */}
                <div className="border-b border-border overflow-x-auto scrollbar-custom">
                  <div className="flex min-w-max">
                    {embedTabs?.map(tab => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveEmbed(tab?.id)}
                        className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                          activeEmbed === tab?.id
                            ? 'text-[#0e7572] border-[#0e7572] bg-[#0e7572]/5'
                            : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={tab?.icon} size={15} />
                        {tab?.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-5">
                  <CodeBlock code={embedSnippets?.[activeEmbed]} language={activeEmbed} />
                  <p className="mt-3 text-xs text-muted-foreground">
                    Paste this snippet into your website's HTML where you want the form to appear.{' '}
                    <button
                      onClick={() => navigate('/embed-settings')}
                      className="text-[#0e7572] hover:underline"
                    >
                      Advanced embed settings →
                    </button>
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="space-y-6">

              {/* Deployment readiness */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-5 border-b border-border">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#0e7572]/10 flex items-center justify-center">
                        <Icon name="ShieldCheck" size={16} color="#0e7572" />
                      </div>
                      <h2 className="text-base font-heading font-semibold text-foreground">Readiness</h2>
                    </div>
                    <span className={`text-lg font-bold ${readinessScore >= 90 ? 'text-[#0e7572]' : readinessScore >= 70 ? 'text-amber-500' : 'text-red-500'}`}>
                      {readinessScore}%
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${readinessScore}%`,
                        background: readinessScore >= 90 ? '#0e7572' : readinessScore >= 70 ? '#f59e0b' : '#ef4444'
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0e7572] inline-block" />{passCount} passed</span>
                    {warnCount > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />{warnCount} warning</span>}
                  </div>
                </div>

                <div className="divide-y divide-border">
                  {readinessChecks?.map(check => (
                    <div key={check?.id} className="flex items-start gap-3 px-5 py-3">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        check?.status === 'pass' ? 'bg-[#0e7572]/10' : 'bg-amber-400/10'
                      }`}>
                        <Icon
                          name={check?.status === 'pass' ? 'Check' : 'AlertTriangle'}
                          size={11}
                          color={check?.status === 'pass' ? '#0e7572' : '#f59e0b'}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground font-medium leading-tight">{check?.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{check?.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Publish status card */}
              <div className={`rounded-xl border p-5 space-y-3 ${
                isPublished
                  ? 'bg-[#0e7572]/5 border-[#0e7572]/25'
                  : 'bg-muted/40 border-border'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isPublished ? 'bg-[#0e7572]/15' : 'bg-muted'
                  }`}>
                    <Icon name={isPublished ? 'Globe' : 'EyeOff'} size={20} color={isPublished ? '#0e7572' : '#6b7280'} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {isPublished ? 'Form is Live' : 'Form is Unpublished'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isPublished ? 'Accepting submissions now' : 'Not visible to the public'}
                    </p>
                  </div>
                </div>

                {isPublished && (
                  <div className="space-y-1.5 text-xs text-muted-foreground">
                    <div className="flex items-center justify-between">
                      <span>Status</span>
                      <span className="text-[#0e7572] font-semibold">Published</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => navigate('/analytics')}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-card border border-border rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="BarChart3" size={13} />
                    Analytics
                  </button>
                  <button
                    onClick={() => navigate('/embed-settings')}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-card border border-border rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="Settings2" size={13} />
                    Embed Settings
                  </button>
                </div>
              </div>

              {/* QR code placeholder */}
              <div className="bg-card border border-border rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0e7572]/10 flex items-center justify-center">
                    <Icon name="QrCode" size={16} color="#0e7572" />
                  </div>
                  <h2 className="text-base font-heading font-semibold text-foreground">QR Code</h2>
                </div>
                <div className="flex flex-col items-center justify-center py-6 bg-muted/40 rounded-lg border border-dashed border-border gap-3">
                  <div className="grid grid-cols-5 gap-1 opacity-30">
                    {Array.from({ length: 25 })?.map((_, i) => (
                      <div key={i} className={`w-4 h-4 rounded-sm ${Math.random() > 0.4 ? 'bg-foreground' : 'bg-transparent'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Scan to open the form on mobile
                  </p>
                </div>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0e7572]/10 border border-[#0e7572]/30 text-[#0e7572] text-sm font-medium rounded-lg hover:bg-[#0e7572]/20 transition-colors">
                  <Icon name="Download" size={15} />
                  Download QR Code
                </button>
              </div>
            </div>
          </div>
        </div>
      </NavigationBar>
    </div>
  );
};

export default PublishScreen;
